const EVENTS_URL = (process.env.REACT_APP_EVENTS_URL || '').replace(/\/+$/, '');

export type TrackedEvent =
  | 'expiry_warning_shown'
  | 'upgrade_cta_shown'
  | 'upgrade_cta_clicked'
  | 'upgrade_button_clicked'
  | 'pro_landing_viewed'
  | 'pro_landing_signin_clicked'
  | 'claim_signin_clicked'
  | 'claim_completed'
  | 'claim_prompt_shown'
  // Anon-uploader email-reminder capture on the post-upload SignInToClaimBanner
  // Zone 2 form. Fires once per successful submission (the frontend gates on
  // idempotent state, and the backend rate-limits per-fingerprint to prevent
  // repeat submissions). Backend allowlist in lambda-events/main.go must
  // accept this event AND allow anon callers — the uploader is not signed in
  // at capture time (that's the entire premise of the feature).
  | 'email_reminder_captured'
  | 'share_signup_cta_clicked'
  | 'notfound_page_shown'
  | 'upload_rejected_too_large'
  // Free-tier activation card for the share-video render feature. Fired once
  // per session per user when the card mounts (`_shown`) and again when the
  // user taps the render button (`_started`). Backend allowlist in
  // lambda-events/main.go must accept both names.
  | 'video_render_card_shown'
  | 'video_render_card_started';

export type EventSource =
  | 'expiry_email'
  | 'in_app_banner'
  | 'in_app_player'
  | 'upload_size_failure'
  | 'direct';

export interface TrackEventPayload {
  source?: EventSource;
  // When true, fire the beacon even if there is no auth token (anon caller).
  // Default is false to preserve existing call-site semantics — most events
  // (claim, upgrade, expiry) are only meaningful for signed-in users and
  // their backend allowlist requires a Bearer token. Set this for events
  // whose entire signal comes from anonymous deep-link traffic
  // (e.g. notfound_page_shown — most 404 viewers arrive via a broken share
  // link in the wild and are not signed in). The backend must also allow
  // anon callers for the event name; otherwise the request is silently
  // dropped at the API edge.
  allowAnon?: boolean;
}

export const trackEvent = async (
  event: TrackedEvent,
  getToken: () => Promise<string | null>,
  payload?: TrackEventPayload,
): Promise<void> => {
  if (!EVENTS_URL) return;
  try {
    const token = await getToken();
    if (!token && !payload?.allowAnon) return;
    const body: Record<string, string> = { event };
    if (payload?.source) body.source = payload.source;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    fetch(EVENTS_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // fire-and-forget; never break UX on telemetry
  }
};

// Attribution capture: read utm_source from URL once on landing, persist to
// sessionStorage so a downstream click (Subscribe button) can stamp the event
// with its origin. Returns true if a value was captured.
const ATTRIBUTION_KEY = 'pw_attribution_source';

const VALID_SOURCES: ReadonlySet<EventSource> = new Set<EventSource>([
  'expiry_email',
  'in_app_banner',
  'in_app_player',
  'upload_size_failure',
  'direct',
]);

export const captureAttributionFromUrl = (search: string): boolean => {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get('utm_source');
    if (!raw) return false;
    if (!VALID_SOURCES.has(raw as EventSource)) return false;
    sessionStorage.setItem(ATTRIBUTION_KEY, raw);
    return true;
  } catch {
    return false;
  }
};

export const readAttribution = (): EventSource => {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (raw && VALID_SOURCES.has(raw as EventSource)) return raw as EventSource;
  } catch {
    // sessionStorage can throw in private mode / SSR
  }
  return 'direct';
};
